import React, { useState, useRef, useEffect } from 'react';
import { useWallet } from './WalletContext';
import { web } from '@klever/sdk-web';
import {
  FaTimes,
  FaChevronLeft,
  FaChevronRight,
  FaCheck,
  FaImage,
  FaPalette,
  FaLink,
  FaCog,
  FaRocket,
  FaInfoCircle,
  FaCheckCircle,
  FaTimesCircle,
  FaSpinner,
  FaGlobe,
  FaTwitter,
  FaDiscord,
  FaTelegram,
  FaPercent,
  FaCubes,
  FaLayerGroup
} from 'react-icons/fa';
import './CreateCollectionModal.css';

const STEPS = [
  { id: 1, title: 'Details', icon: FaLayerGroup },
  { id: 2, title: 'Branding', icon: FaPalette },
  { id: 3, title: 'Metadata', icon: FaLink },
  { id: 4, title: 'Properties', icon: FaCog },
  { id: 5, title: 'Review', icon: FaRocket }
];

const CreateCollectionModal = ({ onClose, onSuccess }) => {
  const { walletAddress } = useWallet();
  const modalRef = useRef(null);

  // Step state
  const [currentStep, setCurrentStep] = useState(1);

  // Form data
  const [formData, setFormData] = useState({
    // Step 1: Details
    name: '',
    ticker: '',
    description: '',
    type: 0, // 0 = NFT, 1 = SFT
    maxSupply: 10000,
    precision: 0,

    // Step 2: Branding
    logo: '',
    banner: '',
    website: '',
    twitter: '',
    discord: '',
    telegram: '',

    // Step 3: Metadata
    metadataUri: '',

    // Step 4: Properties
    canMint: true,
    canBurn: true,
    canPause: false,
    canFreeze: false,
    canWipe: false,
    canChangeOwner: true,
    canAddRoles: true,
    royaltyPercentage: 5,
    royaltyAddress: ''
  });

  // Transaction state
  const [transactionState, setTransactionState] = useState(''); // 'processing', 'success', 'error'
  const [transactionMessage, setTransactionMessage] = useState('');
  const [errors, setErrors] = useState({});

  // Initialize royalty address with wallet address
  useEffect(() => {
    if (walletAddress && !formData.royaltyAddress) {
      setFormData(prev => ({ ...prev, royaltyAddress: walletAddress }));
    }
  }, [walletAddress, formData.royaltyAddress]);

  // Handle outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        if (transactionState !== 'processing') {
          onClose();
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.body.classList.add('modal-open');

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.classList.remove('modal-open');
    };
  }, [onClose, transactionState]);

  // Update form field
  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  // Validate current step
  const validateStep = () => {
    const newErrors = {};

    switch (currentStep) {
      case 1:
        if (!formData.name.trim()) {
          newErrors.name = 'Collection name is required';
        } else if (formData.name.length < 3 || formData.name.length > 32) {
          newErrors.name = 'Name must be 3-32 characters';
        }
        if (!formData.ticker.trim()) {
          newErrors.ticker = 'Ticker is required';
        } else if (!/^[A-Z0-9]{3,10}$/.test(formData.ticker.toUpperCase())) {
          newErrors.ticker = 'Ticker must be 3-10 uppercase letters/numbers';
        }
        if (formData.maxSupply < 1) {
          newErrors.maxSupply = 'Max supply must be at least 1';
        }
        break;
      case 2:
        // Logo and banner are optional but if provided, should be valid
        if (formData.logo && !isValidUri(formData.logo)) {
          newErrors.logo = 'Invalid URL or IPFS hash';
        }
        break;
      case 3:
        // Metadata URI is optional
        if (formData.metadataUri && !isValidUri(formData.metadataUri)) {
          newErrors.metadataUri = 'Invalid URL or IPFS hash';
        }
        break;
      case 4:
        if (formData.royaltyPercentage < 0 || formData.royaltyPercentage > 50) {
          newErrors.royaltyPercentage = 'Royalty must be 0-50%';
        }
        if (formData.royaltyAddress && !/^klv1[a-z0-9]{58}$/.test(formData.royaltyAddress)) {
          newErrors.royaltyAddress = 'Invalid Klever address';
        }
        break;
      default:
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Check if URI is valid
  const isValidUri = (uri) => {
    if (!uri) return true;
    return uri.startsWith('http://') ||
           uri.startsWith('https://') ||
           uri.startsWith('ipfs://') ||
           /^Qm[a-zA-Z0-9]{44}$/.test(uri) ||
           /^bafy[a-zA-Z0-9]{55,}$/.test(uri);
  };

  // Format IPFS URI
  const formatIpfsUri = (uri) => {
    if (!uri) return '';
    if (uri.startsWith('ipfs://')) return uri;
    if (/^Qm[a-zA-Z0-9]{44}$/.test(uri) || /^bafy[a-zA-Z0-9]{55,}$/.test(uri)) {
      return `ipfs://${uri}`;
    }
    return uri;
  };

  // Navigation
  const handleNext = () => {
    if (validateStep()) {
      setCurrentStep(prev => Math.min(prev + 1, 5));
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  // Create collection transaction
  const handleCreate = async () => {
    setTransactionState('processing');
    setTransactionMessage('Building transaction...');

    try {
      // Build URIs object
      const uris = {};
      if (formData.metadataUri) {
        uris.metadata = formatIpfsUri(formData.metadataUri);
      }
      if (formData.website) {
        uris.website = formData.website;
      }
      if (formData.twitter) {
        uris.twitter = formData.twitter;
      }
      if (formData.discord) {
        uris.discord = formData.discord;
      }
      if (formData.telegram) {
        uris.telegram = formData.telegram;
      }

      // Build properties
      const properties = {
        canMint: formData.canMint,
        canBurn: formData.canBurn,
        canPause: formData.canPause,
        canFreeze: formData.canFreeze,
        canWipe: formData.canWipe,
        canChangeOwner: formData.canChangeOwner,
        canAddRoles: formData.canAddRoles
      };

      // Build royalties
      const royalties = {};
      if (formData.royaltyPercentage > 0 && formData.royaltyAddress) {
        royalties.transferPercentage = [
          {
            amount: formData.royaltyPercentage * 100, // Precision 2
            percentage: formData.royaltyPercentage * 100
          }
        ];
      }

      // Build CreateAsset payload (Type 1)
      const payload = {
        type: formData.type, // 0 = NFT, 1 = SFT
        name: formData.name.trim(),
        ticker: formData.ticker.toUpperCase().trim(),
        ownerAddress: walletAddress,
        precision: formData.type === 1 ? formData.precision : 0,
        maxSupply: formData.maxSupply,
        properties: properties
      };

      // Add optional fields
      if (formData.logo) {
        payload.logo = formatIpfsUri(formData.logo);
      }
      if (Object.keys(uris).length > 0) {
        payload.uris = uris;
      }
      if (Object.keys(royalties).length > 0) {
        payload.royalties = royalties;
      }

      const contract = {
        type: 1, // CreateAsset
        payload: payload
      };

      setTransactionMessage('Please sign the transaction...');

      const unsignedTx = await web.buildTransaction([contract]);
      const signedTx = await web.signTransaction(unsignedTx);

      setTransactionMessage('Broadcasting transaction...');
      const response = await web.broadcastTransactions([signedTx]);

      console.log('Create collection response:', response);

      setTransactionState('success');
      setTransactionMessage('Collection created successfully!');

      setTimeout(() => {
        onSuccess();
      }, 2000);

    } catch (error) {
      console.error('Error creating collection:', error);
      setTransactionState('error');
      setTransactionMessage(error.message || 'Failed to create collection');
    }
  };

  // Render step indicator
  const renderStepIndicator = () => (
    <div className="step-indicator">
      {STEPS.map((step, index) => {
        const Icon = step.icon;
        const isActive = step.id === currentStep;
        const isCompleted = step.id < currentStep;

        return (
          <React.Fragment key={step.id}>
            <div className={`step ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''}`}>
              <div className="step-circle">
                {isCompleted ? <FaCheck /> : <Icon />}
              </div>
              <span className="step-title">{step.title}</span>
            </div>
            {index < STEPS.length - 1 && (
              <div className={`step-line ${isCompleted ? 'completed' : ''}`}></div>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );

  // Render Step 1: Details
  const renderStep1 = () => (
    <div className="step-content">
      <h3 className="step-heading">Collection Details</h3>
      <p className="step-description">
        Enter the basic information for your NFT or SFT collection.
      </p>

      <div className="form-group">
        <label htmlFor="name">
          Collection Name <span className="required">*</span>
        </label>
        <input
          type="text"
          id="name"
          placeholder="My Amazing Collection"
          value={formData.name}
          onChange={(e) => updateField('name', e.target.value)}
          maxLength={32}
          className={errors.name ? 'error' : ''}
        />
        {errors.name && <span className="error-message">{errors.name}</span>}
        <span className="hint">{formData.name.length}/32 characters</span>
      </div>

      <div className="form-group">
        <label htmlFor="ticker">
          Ticker/Symbol <span className="required">*</span>
        </label>
        <input
          type="text"
          id="ticker"
          placeholder="MYCOL"
          value={formData.ticker}
          onChange={(e) => updateField('ticker', e.target.value.toUpperCase())}
          maxLength={10}
          className={errors.ticker ? 'error' : ''}
        />
        {errors.ticker && <span className="error-message">{errors.ticker}</span>}
        <span className="hint">3-10 uppercase letters or numbers</span>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="type">Collection Type</label>
          <select
            id="type"
            value={formData.type}
            onChange={(e) => updateField('type', parseInt(e.target.value))}
          >
            <option value={0}>NFT (Non-Fungible)</option>
            <option value={1}>SFT (Semi-Fungible)</option>
          </select>
          <span className="hint">
            {formData.type === 0
              ? 'Each token is unique (1 of 1)'
              : 'Tokens can have multiple copies'
            }
          </span>
        </div>

        <div className="form-group">
          <label htmlFor="maxSupply">Max Supply</label>
          <input
            type="number"
            id="maxSupply"
            min={1}
            value={formData.maxSupply}
            onChange={(e) => updateField('maxSupply', parseInt(e.target.value) || 0)}
            className={errors.maxSupply ? 'error' : ''}
          />
          {errors.maxSupply && <span className="error-message">{errors.maxSupply}</span>}
        </div>
      </div>

      {formData.type === 1 && (
        <div className="form-group">
          <label htmlFor="precision">Precision (Decimals)</label>
          <input
            type="number"
            id="precision"
            min={0}
            max={8}
            value={formData.precision}
            onChange={(e) => updateField('precision', parseInt(e.target.value) || 0)}
          />
          <span className="hint">Number of decimal places (0-8)</span>
        </div>
      )}
    </div>
  );

  // Render Step 2: Branding
  const renderStep2 = () => (
    <div className="step-content">
      <h3 className="step-heading">Branding & Social</h3>
      <p className="step-description">
        Add your collection's visual identity and social links.
      </p>

      <div className="form-group">
        <label htmlFor="logo">
          <FaImage /> Logo Image URL
        </label>
        <input
          type="text"
          id="logo"
          placeholder="https://... or ipfs://... or Qm..."
          value={formData.logo}
          onChange={(e) => updateField('logo', e.target.value)}
          className={errors.logo ? 'error' : ''}
        />
        {errors.logo && <span className="error-message">{errors.logo}</span>}
        <span className="hint">HTTPS URL or IPFS hash for your collection logo</span>
      </div>

      <div className="form-group">
        <label htmlFor="banner">
          <FaImage /> Banner Image URL
        </label>
        <input
          type="text"
          id="banner"
          placeholder="https://... or ipfs://..."
          value={formData.banner}
          onChange={(e) => updateField('banner', e.target.value)}
        />
        <span className="hint">Optional banner for your collection page</span>
      </div>

      <div className="social-links-section">
        <h4>Social Links (Optional)</h4>

        <div className="form-group">
          <label htmlFor="website">
            <FaGlobe /> Website
          </label>
          <input
            type="text"
            id="website"
            placeholder="https://yourproject.com"
            value={formData.website}
            onChange={(e) => updateField('website', e.target.value)}
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="twitter">
              <FaTwitter /> Twitter
            </label>
            <input
              type="text"
              id="twitter"
              placeholder="https://twitter.com/..."
              value={formData.twitter}
              onChange={(e) => updateField('twitter', e.target.value)}
            />
          </div>

          <div className="form-group">
            <label htmlFor="discord">
              <FaDiscord /> Discord
            </label>
            <input
              type="text"
              id="discord"
              placeholder="https://discord.gg/..."
              value={formData.discord}
              onChange={(e) => updateField('discord', e.target.value)}
            />
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="telegram">
            <FaTelegram /> Telegram
          </label>
          <input
            type="text"
            id="telegram"
            placeholder="https://t.me/..."
            value={formData.telegram}
            onChange={(e) => updateField('telegram', e.target.value)}
          />
        </div>
      </div>
    </div>
  );

  // Render Step 3: Metadata
  const renderStep3 = () => (
    <div className="step-content">
      <h3 className="step-heading">Metadata Configuration</h3>
      <p className="step-description">
        Configure how your NFT metadata will be structured and accessed.
      </p>

      <div className="info-box">
        <FaInfoCircle />
        <div>
          <strong>What is Metadata?</strong>
          <p>
            Metadata contains information about each NFT in your collection, including
            the name, description, image, and attributes/traits. It's stored as JSON
            files, typically on IPFS for decentralization.
          </p>
        </div>
      </div>

      <div className="form-group">
        <label htmlFor="metadataUri">
          <FaLink /> Base Metadata URI
        </label>
        <input
          type="text"
          id="metadataUri"
          placeholder="ipfs://Qm... or https://..."
          value={formData.metadataUri}
          onChange={(e) => updateField('metadataUri', e.target.value)}
          className={errors.metadataUri ? 'error' : ''}
        />
        {errors.metadataUri && <span className="error-message">{errors.metadataUri}</span>}
        <span className="hint">
          Base URI for your metadata files. Individual NFT metadata will be at URI/tokenId.json
        </span>
      </div>

      <div className="metadata-example">
        <h4>Example Metadata JSON:</h4>
        <pre>
{`{
  "name": "Cool NFT #1",
  "description": "A very cool NFT",
  "image": "ipfs://Qm.../1.png",
  "attributes": [
    {
      "trait_type": "Background",
      "value": "Blue"
    },
    {
      "trait_type": "Rarity",
      "value": "Rare"
    }
  ]
}`}
        </pre>
      </div>

      <div className="ipfs-guide">
        <h4>How to upload to IPFS:</h4>
        <ol>
          <li>Create your images and metadata JSON files</li>
          <li>Upload to <a href="https://www.pinata.cloud" target="_blank" rel="noopener noreferrer">Pinata</a> or <a href="https://nft.storage" target="_blank" rel="noopener noreferrer">NFT.Storage</a></li>
          <li>Get the CID (Content ID) that starts with "Qm" or "bafy"</li>
          <li>Use the format: ipfs://YOUR_CID</li>
        </ol>
      </div>
    </div>
  );

  // Render Step 4: Properties
  const renderStep4 = () => (
    <div className="step-content">
      <h3 className="step-heading">Properties & Royalties</h3>
      <p className="step-description">
        Configure collection permissions and royalty settings.
      </p>

      <div className="properties-grid">
        <div className="property-toggle">
          <label>
            <input
              type="checkbox"
              checked={formData.canMint}
              onChange={(e) => updateField('canMint', e.target.checked)}
            />
            <span className="toggle-label">Can Mint</span>
          </label>
          <span className="toggle-hint">Allow minting new tokens</span>
        </div>

        <div className="property-toggle">
          <label>
            <input
              type="checkbox"
              checked={formData.canBurn}
              onChange={(e) => updateField('canBurn', e.target.checked)}
            />
            <span className="toggle-label">Can Burn</span>
          </label>
          <span className="toggle-hint">Allow burning tokens</span>
        </div>

        <div className="property-toggle">
          <label>
            <input
              type="checkbox"
              checked={formData.canPause}
              onChange={(e) => updateField('canPause', e.target.checked)}
            />
            <span className="toggle-label">Can Pause</span>
          </label>
          <span className="toggle-hint">Allow pausing transfers</span>
        </div>

        <div className="property-toggle">
          <label>
            <input
              type="checkbox"
              checked={formData.canFreeze}
              onChange={(e) => updateField('canFreeze', e.target.checked)}
            />
            <span className="toggle-label">Can Freeze</span>
          </label>
          <span className="toggle-hint">Allow freezing accounts</span>
        </div>

        <div className="property-toggle">
          <label>
            <input
              type="checkbox"
              checked={formData.canWipe}
              onChange={(e) => updateField('canWipe', e.target.checked)}
            />
            <span className="toggle-label">Can Wipe</span>
          </label>
          <span className="toggle-hint">Allow wiping frozen accounts</span>
        </div>

        <div className="property-toggle">
          <label>
            <input
              type="checkbox"
              checked={formData.canChangeOwner}
              onChange={(e) => updateField('canChangeOwner', e.target.checked)}
            />
            <span className="toggle-label">Can Change Owner</span>
          </label>
          <span className="toggle-hint">Allow transferring ownership</span>
        </div>

        <div className="property-toggle">
          <label>
            <input
              type="checkbox"
              checked={formData.canAddRoles}
              onChange={(e) => updateField('canAddRoles', e.target.checked)}
            />
            <span className="toggle-label">Can Add Roles</span>
          </label>
          <span className="toggle-hint">Allow adding minter roles</span>
        </div>
      </div>

      <div className="royalty-section">
        <h4><FaPercent /> Royalty Settings</h4>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="royaltyPercentage">Royalty Percentage</label>
            <div className="input-with-suffix">
              <input
                type="number"
                id="royaltyPercentage"
                min={0}
                max={50}
                step={0.5}
                value={formData.royaltyPercentage}
                onChange={(e) => updateField('royaltyPercentage', parseFloat(e.target.value) || 0)}
                className={errors.royaltyPercentage ? 'error' : ''}
              />
              <span className="suffix">%</span>
            </div>
            {errors.royaltyPercentage && <span className="error-message">{errors.royaltyPercentage}</span>}
            <span className="hint">Percentage you receive on secondary sales (0-50%)</span>
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="royaltyAddress">Royalty Receiver Address</label>
          <input
            type="text"
            id="royaltyAddress"
            placeholder="klv1..."
            value={formData.royaltyAddress}
            onChange={(e) => updateField('royaltyAddress', e.target.value)}
            className={errors.royaltyAddress ? 'error' : ''}
          />
          {errors.royaltyAddress && <span className="error-message">{errors.royaltyAddress}</span>}
          <span className="hint">Address that will receive royalty payments</span>
        </div>
      </div>
    </div>
  );

  // Render Step 5: Review
  const renderStep5 = () => (
    <div className="step-content">
      <h3 className="step-heading">Review & Create</h3>
      <p className="step-description">
        Review your collection settings before creating.
      </p>

      <div className="review-section">
        <div className="review-group">
          <h4>Collection Details</h4>
          <div className="review-item">
            <span className="review-label">Name</span>
            <span className="review-value">{formData.name}</span>
          </div>
          <div className="review-item">
            <span className="review-label">Ticker</span>
            <span className="review-value">{formData.ticker}</span>
          </div>
          <div className="review-item">
            <span className="review-label">Type</span>
            <span className="review-value">{formData.type === 0 ? 'NFT' : 'SFT'}</span>
          </div>
          <div className="review-item">
            <span className="review-label">Max Supply</span>
            <span className="review-value">{formData.maxSupply.toLocaleString()}</span>
          </div>
        </div>

        {(formData.logo || formData.website) && (
          <div className="review-group">
            <h4>Branding</h4>
            {formData.logo && (
              <div className="review-item">
                <span className="review-label">Logo</span>
                <span className="review-value truncate">{formData.logo}</span>
              </div>
            )}
            {formData.website && (
              <div className="review-item">
                <span className="review-label">Website</span>
                <span className="review-value">{formData.website}</span>
              </div>
            )}
          </div>
        )}

        <div className="review-group">
          <h4>Properties</h4>
          <div className="review-properties">
            {formData.canMint && <span className="property-badge">Can Mint</span>}
            {formData.canBurn && <span className="property-badge">Can Burn</span>}
            {formData.canPause && <span className="property-badge">Can Pause</span>}
            {formData.canFreeze && <span className="property-badge">Can Freeze</span>}
            {formData.canWipe && <span className="property-badge">Can Wipe</span>}
            {formData.canChangeOwner && <span className="property-badge">Can Change Owner</span>}
            {formData.canAddRoles && <span className="property-badge">Can Add Roles</span>}
          </div>
        </div>

        {formData.royaltyPercentage > 0 && (
          <div className="review-group">
            <h4>Royalties</h4>
            <div className="review-item">
              <span className="review-label">Percentage</span>
              <span className="review-value">{formData.royaltyPercentage}%</span>
            </div>
            <div className="review-item">
              <span className="review-label">Receiver</span>
              <span className="review-value truncate">{formData.royaltyAddress}</span>
            </div>
          </div>
        )}

        <div className="fee-notice">
          <FaInfoCircle />
          <span>Creating a collection requires a network fee of approximately 20,000 KLV</span>
        </div>
      </div>

      {/* Transaction Status */}
      {transactionState && (
        <div className={`transaction-status ${transactionState}`}>
          {transactionState === 'processing' && (
            <>
              <FaSpinner className="spin" />
              <span>{transactionMessage}</span>
            </>
          )}
          {transactionState === 'success' && (
            <>
              <FaCheckCircle />
              <span>{transactionMessage}</span>
            </>
          )}
          {transactionState === 'error' && (
            <>
              <FaTimesCircle />
              <span>{transactionMessage}</span>
            </>
          )}
        </div>
      )}
    </div>
  );

  // Render current step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 1: return renderStep1();
      case 2: return renderStep2();
      case 3: return renderStep3();
      case 4: return renderStep4();
      case 5: return renderStep5();
      default: return null;
    }
  };

  return (
    <div className="create-collection-overlay">
      <div className="create-collection-modal" ref={modalRef}>
        <div className="modal-header">
          <h2 className="modal-title">
            <FaCubes /> Create Collection
          </h2>
          <button
            className="modal-close-btn"
            onClick={onClose}
            disabled={transactionState === 'processing'}
          >
            <FaTimes />
          </button>
        </div>

        {renderStepIndicator()}

        <div className="modal-body">
          {renderStepContent()}
        </div>

        <div className="modal-footer">
          {currentStep > 1 && (
            <button
              className="nav-btn back"
              onClick={handleBack}
              disabled={transactionState === 'processing'}
            >
              <FaChevronLeft /> Back
            </button>
          )}

          <div className="footer-spacer"></div>

          {currentStep < 5 ? (
            <button className="nav-btn next" onClick={handleNext}>
              Next <FaChevronRight />
            </button>
          ) : (
            <button
              className="nav-btn create"
              onClick={handleCreate}
              disabled={transactionState === 'processing' || transactionState === 'success'}
            >
              {transactionState === 'processing' ? (
                <>
                  <FaSpinner className="spin" /> Creating...
                </>
              ) : (
                <>
                  <FaRocket /> Create Collection
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateCollectionModal;
